import React, { useEffect, useState } from "react";
import { AlertCircle, CheckCircle, Loader2 } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import ParentAuthLayout from "./ParentAuthLayout";
import {
  persistParentPostAuthRedirect,
  readParentPostAuthRedirect,
} from "../../features/parentFlow/parentFlow";

const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const ParentForgotPasswordPage = () => {
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    persistParentPostAuthRedirect(
      location.state?.redirectTo || readParentPostAuthRedirect()
    );
  }, [location.state?.redirectTo]);

  const handleResetPassword = async () => {
    if (!email) {
      setErrors({ email: "Email is required." });
      return;
    }

    if (!validateEmail(email)) {
      setErrors({ email: "Enter a valid email address." });
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      await axiosInstance.post("api/sendlinkforgetpassword", {
        email,
        isWeb: "1",
      });
      setSuccess(true);
    } catch (error) {
      setErrors({
        submit:
          error?.response?.data?.message ||
          "Unable to send a reset link right now. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const inputClassName =
    "w-full rounded-[22px] border border-[#d8cfbf] bg-[#fffaf4] px-4 py-3 text-base text-[#111827] outline-none transition placeholder:text-[#7c8799] focus:border-[#08059B] focus:bg-white";

  return (
    <ParentAuthLayout
      title="Forgot Password"
      description="Enter your email address and we’ll send you a link to reset your password."
      accent="Reset your password securely"
    >
      <div>
        <h2 className="text-3xl font-bold text-[#111827]">Reset Password</h2>
        <p className="mt-3 text-base leading-7 text-[#475569]">
          We&apos;ll send a reset link to the email address on the account.
        </p>
      </div>

      {!success ? (
        <div className="mt-8 space-y-5">
          <div>
            <label
              htmlFor="parent-forgot-password-email"
              className="mb-2 block text-sm font-bold text-[#374151]"
            >
              Email
            </label>
            <input
              id="parent-forgot-password-email"
              type="email"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
                setErrors({});
              }}
              className={inputClassName}
              placeholder="Enter your email address"
              autoComplete="email"
            />
            {errors.email ? (
              <p className="mt-2 inline-flex items-center gap-2 text-sm font-medium text-red-700">
                <AlertCircle size={16} />
                {errors.email}
              </p>
            ) : null}
          </div>

          {errors.submit ? (
            <div className="rounded-[22px] border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
              {errors.submit}
            </div>
          ) : null}

          <button
            type="button"
            onClick={handleResetPassword}
            disabled={loading}
            className="inline-flex w-full items-center justify-center rounded-full bg-[#08059B] px-6 py-3 text-lg font-bold text-white transition hover:bg-[#01044F] disabled:cursor-not-allowed disabled:opacity-70"
          >
            {loading ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 size={20} className="animate-spin" />
                Sending link...
              </span>
            ) : (
              "Send Reset Link"
            )}
          </button>

          <div className="rounded-[28px] border border-[#e6ded0] bg-[#fcfaf6] px-5 py-4 text-sm leading-7 text-[#475569]">
            Remembered your password?{" "}
            <Link className="font-bold text-[#08059B] underline" to="/parent/login">
              Back to Parent Login
            </Link>
          </div>
        </div>
      ) : (
        <div className="mt-8 rounded-[28px] border border-[#d8ead6] bg-[#f5fff4] p-6 text-center">
          <div className="flex justify-center">
            <CheckCircle size={56} className="text-green-600" />
          </div>
          <h3 className="mt-4 text-2xl font-bold text-[#111827]">
            Reset Link Sent
          </h3>
          <p className="mt-3 text-base leading-7 text-[#475569]">
            We&apos;ve sent a password reset link to{" "}
            <span className="font-bold text-[#08059B]">{email}</span>.
          </p>
          <div className="mt-6 rounded-[28px] border border-[#e6ded0] bg-white px-5 py-4 text-sm leading-7 text-[#475569]">
            Return to{" "}
            <Link className="font-bold text-[#08059B] underline" to="/parent/login">
              Parent Login
            </Link>
          </div>
        </div>
      )}
    </ParentAuthLayout>
  );
};

export default ParentForgotPasswordPage;
